import { Injectable } from '@angular/core';
import { formatCurrency } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { PatientFinancialService } from './patient-financial.service';
import { PatientFinancalBillingService } from './patient-financal-billing.service';
import { UserService } from './user.service';
import { receiptPdfData } from '../utils/receipt-payment-method';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';

const rand = (amount: number) => formatCurrency(amount, 'en-US', 'R ', 'ZAR', '1.2-2');

/** Pays invoices from the credit on a patient's account and files a receipt for each payment used. */
@Injectable({ providedIn: 'root' })
export class PatientCreditService {
  constructor(
    private financial: PatientFinancialService,
    private billing: PatientFinancalBillingService,
    private dialog: MatDialog,
    private users: UserService,
  ) {}

  /**
   * Applies up to `amount` (all the credit the invoice can take when left out). A receipt that cannot be
   * filed or drawn is counted as failed; the credit is applied either way.
   */
  async apply(invoice: any, group: any, amount?: number): Promise<{ applied: number; receipts: number; failed: number }> {
    const response: any = await firstValueFrom(this.financial.applyPatientCredit(invoice.id, group.id, amount));
    const { applied, receipts } = response.data;
    let filed = 0;
    let failed = 0;
    for (const { payment, amount: paid, creditNumber } of receipts) {
      try {
        await this.fileReceipt(invoice, payment, paid, creditNumber, group);
        filed++;
      } catch (error) {
        console.error('Error filing the receipt for applied credit:', error);
        failed++;
      }
    }
    return { applied, receipts: filed, failed };
  }

  /** After an invoice is created: if the patient has credit, ask whether to apply it. Null when not applied. */
  async offer(invoice: any, group: any): Promise<{ applied: number; receipts: number; failed: number } | null> {
    const balance: any = await firstValueFrom(this.financial.getPatientBalance(invoice.patientId));
    const credit = parseFloat(balance?.data?.totalCredits || 0);
    const owing = parseFloat(invoice.outstandingBalance || 0);
    if (credit < 0.01 || owing < 0.01) return null;

    const amount = Math.min(credit, owing);
    const confirmed = await firstValueFrom(this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Apply patient credit',
        message: `The patient has ${rand(credit)} credit. Apply ${rand(amount)} to invoice #${invoice.billingNumber}?`,
        confirmText: 'Apply credit',
        cancelText: 'Not now',
      },
    }).afterClosed());
    if (!confirmed) return null;
    return this.apply(invoice, group, amount);
  }

  private async fileReceipt(invoice: any, payment: any, amount: number, creditNumber: string, group: any): Promise<void> {
    const description = `Paid from credit (overpayment on ${creditNumber}) for invoice ${invoice.billingNumber}`;
    const created: any = await firstValueFrom(this.financial.createBillingDocument({
      patientId: invoice.patientId,
      groupId: group.id,
      documentType: 'receipt',
      issueDate: new Date(),
      dueDate: new Date(),
      subtotal: amount,
      vat: 0,
      totalDue: amount,
      discountType: 'percentage',
      discount: 0,
      notes: description,
      paymentId: payment.id,
      providerId: invoice.providerId || invoice.Provider?.id,
      originalInvoiceNumber: invoice.billingNumber,
      vatExclusive: invoice.vatExclusive === true,
      lineItems: [{ description, quantity: 1, unitPrice: amount, totalAmount: amount, code: 'PAYMENT', codeType: 'procedure', date: new Date() }],
    } as any));
    const receipt = created.data;
    // Dated the day the credit is used and by who used it; the overpayment's own date is on its receipt.
    const data = { ...receiptPdfData(receipt, payment, group), paymentDate: receipt.issueDate || new Date(), user: this.users.user };
    const pdfBytes = await this.billing.generateReceiptPDF(receipt.id, data);
    const pdfBlob = new Blob([pdfBytes as any], { type: 'application/pdf' });
    const fileName = `Receipt-${payment.Patient.name}-${payment.Patient.surname}_${Date.now()}.pdf`;
    await firstValueFrom(this.financial.uploadBillingDocumentPDF(receipt.id, group.id, pdfBlob, fileName));
  }
}
